import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { FREQUENCY } from '@classy/campaign-page-blocks'
import { feesApi } from 'redux/services/fees'
import { RootState } from 'redux/store'
import { initializeApp } from 'redux/actions/initializeApp'
import orderAmountSlice from '../amount/order-amount-slice'
import orderDonorCoveredFeesSlice, {
  DCF_GROSS_ADJUSTMENTS_INITIAL_STATUS,
} from 'modules/order/state/dcf/order-donor-covered-fees-slice'
import {
  setPaymentType,
  setPaymentProcessor,
  setIsAmex,
} from 'modules/order/state/payment-details/payment-details-slice'
import Bugsnag, { NotifiableError } from '@bugsnag/js'

/**
 * This middleware listens to updates to amount to calculate Donor Covered Fees
 */
export const donorCoveredFeesMiddleware = createListenerMiddleware<RootState>()

donorCoveredFeesMiddleware.startListening({
  matcher: isAnyOf(
    initializeApp,
    setPaymentType,
    setPaymentProcessor,
    orderAmountSlice.actions.setValue,
    orderDonorCoveredFeesSlice.actions.setValue,
    setIsAmex,
  ),
  effect: async (action, listenerApi) => {
    const { order, config } = listenerApi.getState()

    const amount = order.amount.value
    const frequency = order.frequency.value
    const paymentType = order.paymentDetails.type
    const paymentProcessor = order.paymentDetails.processor
    const DCFChecked = order.dcf.value.isChecked
    const isAmex = order.paymentDetails.isAmex

    const originalState = listenerApi.getOriginalState()
    const originalAmount = originalState.order.amount.value
    const originalPaymentType = originalState.order.paymentDetails.type
    const originalPaymentProcessor = originalState.order.paymentDetails.processor
    const originalDCFChecked = originalState.order.dcf.value.isChecked
    const isOriginalAmex = originalState.order.paymentDetails.isAmex

    /**
     * Apart from the initializeApp action, for any other action,
     * if there's no state change, stop short.
     */

    if (action.type !== initializeApp.type) {
      if (
        amount === originalAmount &&
        paymentType === originalPaymentType &&
        paymentProcessor === originalPaymentProcessor &&
        DCFChecked === originalDCFChecked &&
        isAmex === isOriginalAmex
      ) {
        return
      }
    }

    // Set DCF isLoading to true so a loading indicator is shown.
    listenerApi.dispatch(
      orderDonorCoveredFeesSlice.actions.setValue({
        ...order.dcf.value,
        isLoading: true,
      }),
    )

    let feeEstimateQueryParams: any

    try {
      feeEstimateQueryParams = {
        campaignId: config.campaignId,
        params: {
          fot: true,
          amount,
          currency: order.currency.value,
          payment_method: paymentType,
          processor_name: paymentProcessor,
          amex: isAmex,
          ...(frequency !== FREQUENCY.ONE_TIME && { recurring: true }),
        },
      }

      const feeEstimateResponse = await listenerApi.dispatch(
        feesApi.endpoints.getFeeEstimate.initiate({
          campaignId: config.campaignId,
          params: {
            fot: true,
            amount,
            currency: order.currency.value,
            payment_method: paymentType,
            processor_name: paymentProcessor,
            amex: isAmex,
            ...(frequency !== FREQUENCY.ONE_TIME && { recurring: true }),
          },
        }),
      )

      const feeData = feeEstimateResponse.data

      if (feeData) {
        const fee = parseFloat(feeData.charge_summary.fee_on_top_amount)

        /**
         * PS make sure total amount has at most 2 decimal digits, otherwise the charge-intent
         * api complains.
         */
        const totalAmount = +(
          amount + (order.dcf.value.isChecked ? fee * order.currency.exchangeRate : 0)
        ).toFixed(2)

        const gross_adjustment = DCFChecked
          ? { ...feeData?.gross_adjustment, raw_initial_gross_amount: amount }
          : DCF_GROSS_ADJUSTMENTS_INITIAL_STATUS

        const currentState = listenerApi.getState()

        if (
          currentState.order.amount.value !== amount ||
          currentState.order.dcf.value.isChecked !== DCFChecked ||
          currentState.order.paymentDetails.processor !== paymentProcessor ||
          currentState.order.frequency.value != frequency ||
          currentState.order.paymentDetails.isAmex !== isAmex
        ) {
          // If the state has changed, this update is stale, so don't update the DCF state.
          // Most likely, this is happening due to a user quickly typing into the amount field.
          return
        }

        // Update current DCF state value with new fees.
        listenerApi.dispatch(
          orderDonorCoveredFeesSlice.actions.setValue({
            ...order.dcf.value,
            fee,
            totalAmount,
            ...gross_adjustment,
            isLoading: false, // Hide loading indicator
          }),
        )
      } else {
        throw new Error('Fee data is null.')
      }
    } catch (error) {
      console.error('DCF failed update fees.', error)

      const currentState = listenerApi.getState()

      // Uncheck DCF and reset state when fee retrieval fails
      // This simulates the user unchecking the DCF checkbox
      listenerApi.dispatch(
        orderDonorCoveredFeesSlice.actions.resetOnFailure({
          baseAmount: order.amount.value,
        }),
      )

      // Add Bugsnag notification
      Bugsnag.notify(error as NotifiableError, (event) => {
        // Add context to help with debugging
        event.addMetadata('dcf-context', {
          action: action.type,
          currentStoreState: {
            amount: currentState.order.amount.value,
            frequency: currentState.order.frequency.value,
            paymentType: currentState.order.paymentDetails.type,
            paymentProcessor: currentState.order.paymentDetails.processor,
            DCFChecked: currentState.order.dcf.value.isChecked,
            isAmex: currentState.order.paymentDetails.isAmex,
            campaignId: currentState.config.campaignId,
          },
          originalParams: feeEstimateQueryParams,
        })
      })
    }
  },
})
